import React, { useEffect, useState } from 'react';
import { formatCurrency } from '../../utils/currencyFormatter';
import { PlusIcon, EditIcon, RefreshCwIcon, EyeIcon, CheckIcon, XIcon, SaveIcon, BuildingIcon, MapPinIcon } from 'lucide-react';
export function ModuleLicensing({
  locale,
  onAddModule,
  onEditModule,
  onViewModule,
  showModuleForm,
  setShowModuleForm,
  selectedModule,
  onModuleUpdate,
  modules = []
}) {
  const [formData, setFormData] = useState({
    id: '',
    name: '',
    licenseType: 'Per-User',
    startDate: '',
    endDate: '',
    renewalMode: 'Manual',
    status: 'Active',
    maxUsers: 0,
    maxVolume: 0,
    volumeMetric: '',
    maxSuppliers: 0,
    cost: 0,
    currency: 'USD',
    entities: [],
    sites: []
  });
  // Available entities and sites
  const availableEntities = ['Global', 'Global Technologies Inc.', 'European Operations Ltd'];
  const availableSites = ['All Sites', 'New York HQ', 'San Francisco Office', 'London Office'];
  useEffect(() => {
    if (selectedModule && showModuleForm) {
      setFormData({
        ...selectedModule
      });
    } else if (showModuleForm) {
      setFormData({
        id: '',
        name: '',
        licenseType: 'Per-User',
        startDate: new Date().toISOString().split('T')[0],
        endDate: new Date(new Date().setFullYear(new Date().getFullYear() + 1)).toISOString().split('T')[0],
        renewalMode: 'Manual',
        status: 'Active',
        maxUsers: 10,
        maxVolume: 0,
        volumeMetric: '',
        maxSuppliers: 0,
        cost: 0,
        currency: 'USD',
        entities: ['Global'],
        sites: ['All Sites']
      });
    }
  }, [selectedModule, showModuleForm]);
  const handleChange = e => {
    const {
      name,
      value,
      type,
      checked
    } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    });
  };
  const handleEntityChange = e => {
    const {
      value,
      checked
    } = e.target;
    if (checked) {
      setFormData({
        ...formData,
        entities: [...formData.entities, value]
      });
    } else {
      setFormData({
        ...formData,
        entities: formData.entities.filter(entity => entity !== value)
      });
    }
  };
  const handleSiteChange = e => {
    const {
      value,
      checked
    } = e.target;
    if (checked) {
      setFormData({
        ...formData,
        sites: [...formData.sites, value]
      });
    } else {
      setFormData({
        ...formData,
        sites: formData.sites.filter(site => site !== value)
      });
    }
  };
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    let updatedModules;
    if (selectedModule) {
      updatedModules = modules.map(m => m.id === selectedModule.id ? {
        ...m,
        ...formData,
        modifiedAt: new Date().toISOString()
      } : m);
    } else {
      const newModule = {
        ...formData,
        id: `mod-${Date.now()}`,
        createdAt: new Date().toISOString(),
        modifiedAt: new Date().toISOString()
      };
      updatedModules = [...modules, newModule];
    }
    console.log('Submitting updated modules:', updatedModules);
    onModuleUpdate(updatedModules);
    setShowModuleForm(false);
  };
  const handleRenewModule = moduleId => {
    const updatedModules = modules.map(module => {
      if (module.id === moduleId) {
        const currentEndDate = new Date(module.endDate);
        const newEndDate = new Date(currentEndDate.setFullYear(currentEndDate.getFullYear() + 1));
        return {
          ...module,
          endDate: newEndDate.toISOString().split('T')[0],
          status: 'Active'
        };
      }
      return module;
    });
    console.log('Renewing module:', updatedModules);
    onModuleUpdate(updatedModules);
  };
  const getStatusBadge = status => {
    switch (status) {
      case 'Active':
        return {
          bg: 'bg-green-100',
          text: 'text-green-800',
          icon: <CheckIcon className="h-4 w-4 mr-1" data-id="element-15651" />
        };
      case 'Expiring Soon':
        return {
          bg: 'bg-yellow-100',
          text: 'text-yellow-800',
          icon: <RefreshCwIcon className="h-4 w-4 mr-1" data-id="element-15652" />
        };
      case 'Expired':
        return {
          bg: 'bg-red-100',
          text: 'text-red-800',
          icon: <XIcon className="h-4 w-4 mr-1" data-id="element-15653" />
        };
      case 'Inactive':
        return {
          bg: 'bg-gray-100',
          text: 'text-gray-800',
          icon: <XIcon className="h-4 w-4 mr-1" data-id="element-15654" />
        };
      default:
        return {
          bg: 'bg-gray-100',
          text: 'text-gray-800',
          icon: null
        };
    }
  };
  return <div className="space-y-6" data-id="element-15655">
      {showModuleForm ? <div className="bg-white shadow sm:rounded-lg" data-id="element-15656">
          <div className="px-4 py-5 sm:p-6" data-id="element-15657">
            <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-15658">
              {selectedModule ? 'Edit Module License' : 'Add Module License'}
            </h3>
            <form className="mt-5 space-y-6" onSubmit={handleSubmit} data-id="element-15659">
              <div className="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6" data-id="element-15660">
                {/* Module Name */}
                <div className="sm:col-span-3" data-id="element-15661">
                  <label htmlFor="name" className="block text-sm font-medium text-gray-700" data-id="element-15662">
                    Module Name
                  </label>
                  <div className="mt-1" data-id="element-15663">
                    <input type="text" name="name" id="name" value={formData.name} onChange={handleChange} className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" required data-id="element-15664" />
                  </div>
                </div>
                {/* License Type */}
                <div className="sm:col-span-3" data-id="element-15665">
                  <label htmlFor="licenseType" className="block text-sm font-medium text-gray-700" data-id="element-15666">
                    License Type
                  </label>
                  <div className="mt-1" data-id="element-15667">
                    <select id="licenseType" name="licenseType" value={formData.licenseType} onChange={handleChange} className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" data-id="element-15668">
                      <option value="Per-User" data-id="element-15669">Per-User</option>
                      <option value="Volume-Based" data-id="element-15670">Volume-Based</option>
                      <option value="Flat-Fee" data-id="element-15671">Flat-Fee</option>
                    </select>
                  </div>
                </div>
                {/* Start Date */}
                <div className="sm:col-span-3" data-id="element-15672">
                  <label htmlFor="startDate" className="block text-sm font-medium text-gray-700" data-id="element-15673">
                    Start Date
                  </label>
                  <div className="mt-1" data-id="element-15674">
                    <input type="date" name="startDate" id="startDate" value={formData.startDate} onChange={handleChange} className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" required data-id="element-15675" />
                  </div>
                </div>
                {/* End Date */}
                <div className="sm:col-span-3" data-id="element-15676">
                  <label htmlFor="endDate" className="block text-sm font-medium text-gray-700" data-id="element-15677">
                    End Date
                  </label>
                  <div className="mt-1" data-id="element-15678">
                    <input type="date" name="endDate" id="endDate" value={formData.endDate} onChange={handleChange} className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" required data-id="element-15679" />
                  </div>
                </div>
                {/* Renewal Mode */}
                <div className="sm:col-span-3" data-id="element-15680">
                  <label htmlFor="renewalMode" className="block text-sm font-medium text-gray-700" data-id="element-15681">
                    Renewal Mode
                  </label>
                  <div className="mt-1" data-id="element-15682">
                    <select id="renewalMode" name="renewalMode" value={formData.renewalMode} onChange={handleChange} className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" data-id="element-15683">
                      <option value="Auto" data-id="element-15684">Auto</option>
                      <option value="Manual" data-id="element-15685">Manual</option>
                    </select>
                  </div>
                </div>
                {/* Status */}
                <div className="sm:col-span-3" data-id="element-15686">
                  <label htmlFor="status" className="block text-sm font-medium text-gray-700" data-id="element-15687">
                    Status
                  </label>
                  <div className="mt-1" data-id="element-15688">
                    <select id="status" name="status" value={formData.status} onChange={handleChange} className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" data-id="element-15689">
                      <option value="Active" data-id="element-15690">Active</option>
                      <option value="Inactive" data-id="element-15691">Inactive</option>
                      <option value="Expiring Soon" data-id="element-15692">Expiring Soon</option>
                      <option value="Expired" data-id="element-15693">Expired</option>
                    </select>
                  </div>
                </div>
                {/* Conditional fields based on License Type */}
                {formData.licenseType === 'Per-User' && <div className="sm:col-span-3" data-id="element-15694">
                    <label htmlFor="maxUsers" className="block text-sm font-medium text-gray-700" data-id="element-15695">
                      Max Users
                    </label>
                    <div className="mt-1" data-id="element-15696">
                      <input type="number" name="maxUsers" id="maxUsers" value={formData.maxUsers} onChange={handleChange} min="0" className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" data-id="element-15697" />
                    </div>
                  </div>}
                {formData.licenseType === 'Volume-Based' && <>
                    <div className="sm:col-span-2" data-id="element-15698">
                      <label htmlFor="maxVolume" className="block text-sm font-medium text-gray-700" data-id="element-15699">
                        Max Volume
                      </label>
                      <div className="mt-1" data-id="element-15700">
                        <input type="number" name="maxVolume" id="maxVolume" value={formData.maxVolume} onChange={handleChange} min="0" className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" data-id="element-15701" />
                      </div>
                    </div>
                    <div className="sm:col-span-2" data-id="element-15702">
                      <label htmlFor="volumeMetric" className="block text-sm font-medium text-gray-700" data-id="element-15703">
                        Volume Metric
                      </label>
                      <div className="mt-1" data-id="element-15704">
                        <input type="text" name="volumeMetric" id="volumeMetric" value={formData.volumeMetric} onChange={handleChange} placeholder="e.g., Invoices, Documents" className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" data-id="element-15705" />
                      </div>
                    </div>
                  </>}
                {formData.licenseType === 'Flat-Fee' && <div className="sm:col-span-3" data-id="element-15706">
                    <label htmlFor="maxSuppliers" className="block text-sm font-medium text-gray-700" data-id="element-15707">
                      Max Suppliers
                    </label>
                    <div className="mt-1" data-id="element-15708">
                      <input type="number" name="maxSuppliers" id="maxSuppliers" value={formData.maxSuppliers} onChange={handleChange} min="0" className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" data-id="element-15709" />
                    </div>
                  </div>}
                {/* Cost */}
                <div className="sm:col-span-2" data-id="element-15710">
                  <label htmlFor="cost" className="block text-sm font-medium text-gray-700" data-id="element-15711">
                    Cost
                  </label>
                  <div className="mt-1" data-id="element-15712">
                    <input type="number" name="cost" id="cost" value={formData.cost} onChange={handleChange} min="0" step="0.01" className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" data-id="element-15713" />
                  </div>
                </div>
                {/* Currency */}
                <div className="sm:col-span-2" data-id="element-15714">
                  <label htmlFor="currency" className="block text-sm font-medium text-gray-700" data-id="element-15715">
                    Currency
                  </label>
                  <div className="mt-1" data-id="element-15716">
                    <select id="currency" name="currency" value={formData.currency} onChange={handleChange} className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" data-id="element-15717">
                      <option value="USD" data-id="element-15718">USD</option>
                      <option value="EUR" data-id="element-15719">EUR</option>
                      <option value="GBP" data-id="element-15720">GBP</option>
                      <option value="AUD" data-id="element-15721">AUD</option>
                    </select>
                  </div>
                </div>
                {/* Entities */}
                <div className="sm:col-span-3" data-id="element-15722">
                  <label className="block text-sm font-medium text-gray-700" data-id="element-15723">
                    <BuildingIcon className="h-4 w-4 inline mr-1" data-id="element-15724" />
                    Entities
                  </label>
                  <div className="mt-2 space-y-2" data-id="element-15725">
                    {availableEntities.map(entity => <div key={entity} className="flex items-center" data-id="element-15726">
                        <input id={`entity-${entity}`} type="checkbox" value={entity} checked={formData.entities.includes(entity)} onChange={handleEntityChange} className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded" data-id="element-15727" />
                        <label htmlFor={`entity-${entity}`} className="ml-2 block text-sm text-gray-900" data-id="element-15728">
                          {entity}
                        </label>
                      </div>)}
                  </div>
                </div>
                {/* Sites */}
                <div className="sm:col-span-3" data-id="element-15729">
                  <label className="block text-sm font-medium text-gray-700" data-id="element-15730">
                    <MapPinIcon className="h-4 w-4 inline mr-1" data-id="element-15731" />
                    Sites
                  </label>
                  <div className="mt-2 space-y-2" data-id="element-15732">
                    {availableSites.map(site => <div key={site} className="flex items-center" data-id="element-15733">
                        <input id={`site-${site}`} type="checkbox" value={site} checked={formData.sites.includes(site)} onChange={handleSiteChange} className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded" data-id="element-15734" />
                        <label htmlFor={`site-${site}`} className="ml-2 block text-sm text-gray-900" data-id="element-15735">
                          {site}
                        </label>
                      </div>)}
                  </div>
                </div>
              </div>
              <div className="flex justify-end space-x-3" data-id="element-15736">
                <button type="button" onClick={() => setShowModuleForm(false)} className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-15737">
                  Cancel
                </button>
                <button type="submit" className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-15738">
                  <SaveIcon className="h-4 w-4 mr-2" data-id="element-15739" />
                  {selectedModule ? 'Update License' : 'Add License'}
                </button>
              </div>
            </form>
          </div>
        </div> : <div className="bg-white shadow overflow-hidden sm:rounded-lg" data-id="element-15740">
          <div className="px-4 py-5 sm:p-6" data-id="element-15741">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3" data-id="element-15742">
              {modules.map(module => {
            const badge = getStatusBadge(module.status);
            return <div key={module.id} className="relative rounded-lg border border-gray-300 bg-white px-6 py-5 shadow-sm flex flex-col space-y-3" data-id="element-15743">
                    <div className="flex justify-between items-start" data-id="element-15744">
                      <h3 className="text-sm font-medium text-gray-900" data-id="element-15745">
                        {module.name}
                      </h3>
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${badge.bg} ${badge.text}`} data-id="element-15746">
                        {badge.icon}
                        {module.status}
                      </span>
                    </div>
                    <div className="text-xs text-gray-500 space-y-1" data-id="element-15747">
                      <div data-id="element-15748">{module.licenseType}</div>
                      <div data-id="element-15749">
                        {new Date(module.startDate).toLocaleDateString(locale)}{' '}
                        - {new Date(module.endDate).toLocaleDateString(locale)}
                      </div>
                      <div data-id="element-15750">
                        {formatCurrency(module.cost, module.currency, locale)}
                      </div>
                    </div>
                    <div className="flex space-x-2" data-id="element-15751">
                      <button onClick={() => onEditModule(module)} className="inline-flex items-center px-3 py-1.5 border border-gray-300 shadow-sm text-xs font-medium rounded text-gray-700 bg-white hover:bg-gray-50" data-id="element-15752">
                        <EditIcon className="h-3 w-3 mr-1" data-id="element-15753" />
                        Edit
                      </button>
                      <button onClick={() => handleRenewModule(module.id)} className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded shadow-sm text-white bg-indigo-600 hover:bg-indigo-700" data-id="element-15754">
                        <RefreshCwIcon className="h-3 w-3 mr-1" data-id="element-15755" />
                        Renew
                      </button>
                    </div>
                  </div>;
          })}
            </div>
          </div>
        </div>}
    </div>;
}